// Opciones y etiquetas de las promos de referido: eventos y premios por canal, y cómo paga.
import type {
    ReferralChannel,
    ReferralRewardType,
    ReferralTrigger,
} from '../api/promotionsContract';

export interface TriggerOption {
    value: ReferralTrigger;
    /** Título de la tarjeta del evento. */
    label: string;
    hint: string;
    /** Columna Evento de la lista. */
    short: string;
}

/** Eventos de cada canal, en el orden del mock; el back rechaza los de otro canal. */
export const TRIGGER_OPTIONS: Readonly<Record<ReferralChannel, readonly TriggerOption[]>> = {
    SALE: [
        {
            value: 'FIRST_PURCHASE',
            label: 'Hace su primera compra en SureKeep',
            hint: 'Se dispara cuando su primera venta queda cobrada.',
            short: 'Primera compra',
        },
        {
            value: 'ACCOUNT_OPENED',
            label: 'Abre su cuenta Fiado',
            hint: 'Aunque no compre: se dispara al quedar abierta su cuenta.',
            short: 'Abre su cuenta',
        },
        {
            value: 'ANY_PURCHASE',
            label: 'Hace cualquier compra',
            hint: 'Cada compra dentro de la ventana cuenta, hasta el máximo de premios.',
            short: 'Cualquier compra',
        },
        {
            value: 'NTH_PURCHASE',
            label: 'Vuelve a comprar',
            hint: 'Se dispara en su compra número N; premia que se quede como cliente.',
            short: 'Compra número N',
        },
    ],
    CREDIT: [
        {
            value: 'CREDIT_ACTIVATED',
            label: 'Activa su crédito',
            hint: 'Se dispara cuando su crédito queda activo.',
            short: 'Activa su crédito',
        },
        {
            value: 'FIRST_INSTALLMENT',
            label: 'Paga su primer cupón a tiempo',
            hint: 'Se dispara cuando paga a tiempo su primer cupón.',
            short: 'Primer cupón a tiempo',
        },
        {
            value: 'INSTALLMENT_STREAK',
            label: 'Paga N cupones seguidos a tiempo',
            hint: 'Se dispara al completar la racha; premia que pague puntual.',
            short: 'Racha de cupones',
        },
    ],
};

export type ReferralRole = 'referrerReward' | 'referredReward';

export interface RewardOption {
    value: ReferralRewardType;
    label: string;
}

/** Premios de cada rol por canal; cupón y descuento en esta compra solo existen en venta. */
export const REWARD_OPTIONS: Readonly<
    Record<ReferralChannel, Readonly<Record<ReferralRole, readonly RewardOption[]>>>
> = {
    SALE: {
        referrerReward: [
            { value: 'FIXED_BONUS', label: 'Bono fijo' },
            { value: 'PERCENT_BONUS', label: '% del ticket de su referido' },
            { value: 'COUPON', label: 'Cupón para su próxima compra' },
        ],
        referredReward: [
            { value: 'SALE_DISCOUNT', label: 'Descuento en esta compra' },
            { value: 'FIXED_BONUS', label: 'Bono fijo' },
            { value: 'PERCENT_BONUS', label: '% de su ticket' },
            { value: 'COUPON', label: 'Cupón próxima compra' },
        ],
    },
    CREDIT: {
        referrerReward: [
            { value: 'FIXED_BONUS', label: 'Bono fijo' },
            { value: 'PERCENT_BONUS', label: '% de lo que pague su referido' },
        ],
        referredReward: [
            { value: 'FIXED_BONUS', label: 'Bono fijo' },
            { value: 'PERCENT_BONUS', label: '% de lo que pague' },
        ],
    },
};

/** Cómo paga el referido la compra que cuenta. */
/** `CUSTOM` = una mezcla guardada que estas opciones no representan; se conserva tal cual. */
export type PayWith = 'ANY' | 'CASH' | 'SUREKEEP' | 'OTHER' | 'CUSTOM';

export const PAY_WITH_OPTIONS: readonly { value: Exclude<PayWith, 'CUSTOM'>; label: string }[] = [
    { value: 'ANY', label: 'Cualquier forma' },
    { value: 'CASH', label: 'Contado' },
    { value: 'SUREKEEP', label: 'Crédito SureKeep' },
    { value: 'OTHER', label: 'Otra financiera' },
];

export const CASH_METHODS = ['CASH', 'CARD', 'WIRE'] as const;
export type CashMethod = (typeof CASH_METHODS)[number];

export const CASH_METHOD_LABEL: Record<CashMethod, string> = {
    CASH: 'Efectivo',
    CARD: 'Tarjeta',
    WIRE: 'Transferencia',
};

export const FINANCIERS = ['PAYJOY', 'CREDIYA', 'LESPAGO', 'INNOVA'] as const;
export type Financier = (typeof FINANCIERS)[number];

export const FINANCIER_LABEL: Record<Financier, string> = {
    PAYJOY: 'PayJoy',
    CREDIYA: 'CrediYa',
    LESPAGO: 'LesPago',
    INNOVA: 'Innova',
};

/** Etiqueta corta del evento para la lista: «Compra n.º 3», «3 cupones seguidos». */
export function triggerShortLabel(config: {
    trigger: ReferralTrigger;
    purchaseNumber: number | null;
    installmentStreak: number | null;
}): string {
    if (config.trigger === 'NTH_PURCHASE')
        return `Compra n.º ${String(config.purchaseNumber ?? 'N')}`;
    if (config.trigger === 'INSTALLMENT_STREAK')
        return `${String(config.installmentStreak ?? 'N')} cupones seguidos a tiempo`;
    const all = [...TRIGGER_OPTIONS.SALE, ...TRIGGER_OPTIONS.CREDIT];
    return all.find((option) => option.value === config.trigger)?.short ?? config.trigger;
}

/** El evento depende de una compra o un pago: al abrir cuenta no hay ninguno. */
export function triggerHasPurchase(trigger: ReferralTrigger): boolean {
    return trigger !== 'ACCOUNT_OPENED';
}
